import { useRef, useEffect, useCallback, useState } from 'react';
import { getChatWebSocketBaseUrl } from '@services/chatService';
import {
  STT_WS_PATH,
  STT_WS_QUERY_PARAM,
  STT_COMMAND,
  STT_EVENT,
  STT_STATUS,
  STT_SAMPLE_RATE,
  PCM_WORKLET_MODULE_URL,
  PCM_WORKLET_PROCESSOR_NAME,
  PCM_FRAME_DURATION_MS,
  STT_FINAL_WAIT_MS,
  CHAT_SESSION_STORAGE_KEY,
  MIC_ERROR_MESSAGE,
} from '@constants/speechToText';
import type { VoicePermissionErrorType } from '@pages/chatbot/components/VoicePermissionModal';
import { getMediaErrorCode, mapMediaErrorToType, type MicErrorCode } from './useSpeechToText';

/** Message JSON phẳng server trả về qua WS STT - xem docs/websocket-stt-realtime.md */
interface SttWsMessage {
  typeMessage?: string;
  conversationId?: string;
  status?: string;
  text?: string;
  event?: string;
  isFinal?: boolean;
}

interface UseGrpcSpeechToTextOptions {
  onResult: (text: string) => void;
  /**
   * Gọi khi server kết thúc phiên (event NO_SPEECH_TIMEOUT hoặc server đóng WS) → auto-submit
   * nội dung đang có. Client KHÔNG tự dừng theo thời gian; FINAL chỉ chốt câu, không dừng mic.
   */
  onSilenceTimeout?: () => void;
  /** Giữ để tương thích interface; ngôn ngữ STT nay do backend quyết định qua pipelineCode (config phía BE), không truyền qua WS. */
  lang?: string;
  /** Giữ để tương thích interface; không dùng - timeout hoàn toàn do server (NO_SPEECH_TIMEOUT). */
  silenceTimeoutMs?: number;
  /** Giữ để tương thích interface; không dùng - timeout hoàn toàn do server (NO_SPEECH_TIMEOUT). */
  maxDurationMs?: number;
  /** code/onErrorType giống useSpeechToText để UI chọn đúng dialog lỗi mic. */
  onError?: (message: string, code?: MicErrorCode) => void;
  onErrorType?: (type: VoicePermissionErrorType) => void;
}

interface UseGrpcSpeechToTextReturn {
  start: () => void;
  stop: () => void;
  stopAndDiscard: () => void;
  isListening: boolean;
  isSupported: boolean;
}

/**
 * Hook STT - User nói → Text, qua gRPC Viettel TV360 (STT server-side) thay cho Web Speech API.
 * Giữ nguyên interface/options của useSpeechToText để nơi dùng (useVoiceInteractionController)
 * chỉ cần đổi import, không cần sửa signature.
 *
 * Mọi nhánh mic tự dừng đều auto-submit như bản cũ, xem UseGrpcSpeechToTextOptions.onSilenceTimeout.
 */
export function useGrpcSpeechToText(options: UseGrpcSpeechToTextOptions): UseGrpcSpeechToTextReturn {
  const {
    onResult,
    onSilenceTimeout,
    onError,
    onErrorType,
  } = options;

  const [isListening, setIsListening] = useState(false);
  const [isSupported, setIsSupported] = useState(false);

  const onResultRef = useRef(onResult);
  const onErrorRef = useRef(onError);
  const onErrorTypeRef = useRef(onErrorType);
  const onSilenceTimeoutRef = useRef(onSilenceTimeout);
  useEffect(() => { onResultRef.current = onResult; }, [onResult]);
  useEffect(() => { onErrorRef.current = onError; }, [onError]);
  useEffect(() => { onErrorTypeRef.current = onErrorType; }, [onErrorType]);
  useEffect(() => { onSilenceTimeoutRef.current = onSilenceTimeout; }, [onSilenceTimeout]);

  // Guard chống race-condition khi user bấm stop trong lúc đang chờ getUserMedia/addModule resolve
  const shouldStartRef = useRef(false);
  // Chặn mọi onResult/onmessage còn treo sau khi stopAndDiscard() được gọi
  const discardRef = useRef(false);
  // Text các câu server đã chốt (isFinal) trong lần bật mic hiện tại
  const committedTextRef = useRef('');

  const wsRef = useRef<WebSocket | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const sourceNodeRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const workletNodeRef = useRef<AudioWorkletNode | null>(null);
  const silentGainRef = useRef<GainNode | null>(null);

  // Khác null khi user đã bấm dừng (gửi STOP) và đang giữ WS chờ server trả nốt text cuối.
  // notify: khi chốt phiên có gọi onSilenceTimeout (auto-submit) hay không.
  const finishingRef = useRef<{ notify: boolean } | null>(null);
  const finalWaitTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const supported = !!(
      typeof navigator.mediaDevices?.getUserMedia === 'function' &&
      (window.AudioContext || (window as any).webkitAudioContext) &&
      typeof AudioWorkletNode !== 'undefined' &&
      typeof WebSocket !== 'undefined'
    );
    setIsSupported(supported);
  }, []);

  const teardownAudioGraph = useCallback(() => {
    if (workletNodeRef.current) {
      workletNodeRef.current.port.onmessage = null;
      workletNodeRef.current.disconnect();
      workletNodeRef.current = null;
    }
    if (sourceNodeRef.current) {
      sourceNodeRef.current.disconnect();
      sourceNodeRef.current = null;
    }
    if (silentGainRef.current) {
      silentGainRef.current.disconnect();
      silentGainRef.current = null;
    }
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach((track) => track.stop());
      mediaStreamRef.current = null;
    }
    if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
      audioContextRef.current.close().catch(() => {});
    }
    audioContextRef.current = null;
  }, []);

  /**
   * Dừng thu + đóng kết nối ngay. Bản thân hàm này không gọi onSilenceTimeout - nơi gọi tự gọi
   * thêm sau khi dừng xong nếu cần auto-submit.
   */
  const stopCapture = useCallback((sendStopCommand: boolean) => {
    if (finalWaitTimerRef.current) {
      clearTimeout(finalWaitTimerRef.current);
      finalWaitTimerRef.current = null;
    }
    finishingRef.current = null;

    const ws = wsRef.current;
    if (ws) {
      if (sendStopCommand && ws.readyState === WebSocket.OPEN) {
        try {
          ws.send(JSON.stringify({ command: STT_COMMAND.STOP }));
        } catch (e) {
          console.error('[STT-gRPC] Error sending STOP command:', e);
        }
      }
      ws.onmessage = null;
      ws.onerror = null;
      ws.onclose = null;
      try {
        ws.close();
      } catch (e) {
        console.error('[STT-gRPC] Error closing socket:', e);
      }
    }
    wsRef.current = null;

    teardownAudioGraph();
    setIsListening(false);
  }, [teardownAudioGraph]);

  /** Chốt phiên đang chờ text cuối: đóng WS, rồi auto-submit nếu phiên được đánh dấu notify. */
  const finalizeCapture = useCallback(() => {
    const notify = finishingRef.current?.notify ?? false;
    stopCapture(false);
    if (notify) {
      onSilenceTimeoutRef.current?.();
    }
  }, [stopCapture]);

  /**
   * Dừng thu nhưng CHƯA đóng WS ngay: tắt mic, gửi STOP rồi giữ kết nối chờ server trả nốt text
   * (event FINAL / END_OF_UTTERANCE), server tự đóng WS hoặc hết STT_FINAL_WAIT_MS mới chốt phiên.
   * Tránh mất text khi server còn đang xử lý ASR mà client đã đóng kết nối.
   * isListening vẫn giữ true trong lúc chờ để không mở phiên mới chồng lên phiên cũ.
   */
  const gracefulStop = useCallback((notify: boolean) => {
    if (finishingRef.current) {
      // Đang chờ text cuối mà lại bị yêu cầu dừng (vd user bấm mic lần nữa) → chốt luôn.
      finalizeCapture();
      return;
    }

    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) {
      stopCapture(false);
      if (notify) {
        onSilenceTimeoutRef.current?.();
      }
      return;
    }

    teardownAudioGraph();
    finishingRef.current = { notify };

    try {
      ws.send(JSON.stringify({ command: STT_COMMAND.STOP }));
    } catch (e) {
      console.error('[STT-gRPC] Error sending STOP command:', e);
    }

    finalWaitTimerRef.current = setTimeout(() => {
      console.log(`[STT-gRPC] No final text after ${STT_FINAL_WAIT_MS}ms since STOP - closing`);
      finalizeCapture();
    }, STT_FINAL_WAIT_MS);
  }, [teardownAudioGraph, stopCapture, finalizeCapture]);

  const start = useCallback(() => {
    if (!isSupported) {
      onErrorTypeRef.current?.('BROWSER_NOT_SUPPORTED');
      onErrorRef.current?.(MIC_ERROR_MESSAGE.UNSUPPORTED, 'unsupported');
      return;
    }
    if (isListening) return;

    discardRef.current = false;
    shouldStartRef.current = true;
    committedTextRef.current = '';

    // sampleRate là "ideal" chứ không ép cứng - nhiều mic/driver không capture đúng 16000Hz ở tầng
    // phần cứng và sẽ ném OverconstrainedError nếu ép exact. AudioContext({sampleRate: 16000}) ở
    // dưới mới là nơi đảm bảo audio được resample về đúng 16kHz trước khi gửi lên server.
    navigator.mediaDevices.getUserMedia({ audio: { channelCount: 1, sampleRate: { ideal: STT_SAMPLE_RATE } } })
      .then(async (stream) => {
        if (!shouldStartRef.current) {
          console.log('[STT-gRPC] getUserMedia resolved but STT was stopped/cancelled in the meantime');
          stream.getTracks().forEach((track) => track.stop());
          return;
        }

        const conversationId = sessionStorage.getItem(CHAT_SESSION_STORAGE_KEY.CONVERSATION_ID);
        const token = sessionStorage.getItem(CHAT_SESSION_STORAGE_KEY.ACCESS_TOKEN);
        if (!conversationId || !token) {
          console.error('[STT-gRPC] Missing conversationId/token in sessionStorage');
          stream.getTracks().forEach((track) => track.stop());
          onErrorRef.current?.(MIC_ERROR_MESSAGE.MISSING_SESSION);
          return;
        }

        try {
          mediaStreamRef.current = stream;
          const AudioContextCtor: typeof AudioContext =
            window.AudioContext || (window as any).webkitAudioContext;
          // Ép AudioContext chạy ở đúng 16kHz (Viettel STT không tự resample) - trình duyệt sẽ tự
          // resample tín hiệu mic (thường 44.1/48kHz) về context rate này.
          const audioContext = new AudioContextCtor({ sampleRate: STT_SAMPLE_RATE });
          audioContextRef.current = audioContext;

          // sampleRate truyền cho worklet qua processorOptions để nó tự tính số sample cho mỗi
          // cụm ~100ms; thực tế luôn là STT_SAMPLE_RATE vì audioContext đã bị ép ở trên.
          await audioContext.audioWorklet.addModule(PCM_WORKLET_MODULE_URL);

          if (!shouldStartRef.current) {
            teardownAudioGraph();
            return;
          }

          const wsUrl = `${getChatWebSocketBaseUrl()}${STT_WS_PATH}` +
            `?${STT_WS_QUERY_PARAM.CONVERSATION_ID}=${encodeURIComponent(conversationId)}` +
            `&${STT_WS_QUERY_PARAM.TOKEN}=${encodeURIComponent(token)}`;
          const ws = new WebSocket(wsUrl);
          wsRef.current = ws;

          const sourceNode = audioContext.createMediaStreamSource(stream);
          sourceNodeRef.current = sourceNode;

          const workletNode = new AudioWorkletNode(audioContext, PCM_WORKLET_PROCESSOR_NAME, {
            processorOptions: { sampleRate: audioContext.sampleRate, frameDurationMs: PCM_FRAME_DURATION_MS },
            channelCount: 1,
            channelCountMode: 'explicit',
          });
          workletNodeRef.current = workletNode;

          workletNode.port.onmessage = (event: MessageEvent) => {
            if (discardRef.current) return;
            const buffer = event.data as ArrayBuffer;

            const socket = wsRef.current;
            if (socket && socket.readyState === WebSocket.OPEN) {
              socket.send(buffer);
            }
          };

          sourceNode.connect(workletNode);
          // Nối qua GainNode âm lượng 0 → destination để một số trình duyệt (Chrome) không
          // "đóng băng" AudioWorklet khi không có node nào nối tới destination.
          const silentGain = audioContext.createGain();
          silentGain.gain.value = 0;
          silentGainRef.current = silentGain;
          workletNode.connect(silentGain);
          silentGain.connect(audioContext.destination);

          ws.onopen = () => {
            if (!shouldStartRef.current) {
              ws.close();
              return;
            }
            console.log('[STT-gRPC] WebSocket connected, start streaming PCM16LE');
            setIsListening(true);
          };

          ws.onmessage = (event) => {
            if (discardRef.current) return;

            let message: SttWsMessage;
            try {
              message = JSON.parse(event.data);
            } catch (e) {
              console.warn('[STT-gRPC] Non-JSON message ignored:', event.data);
              return;
            }

            // Message là JSON phẳng { typeMessage, conversationId, status, text, event, isFinal }
            // - KHÔNG bọc trong field "message" (khác với WS chat thường).
            // Chuẩn hoá event để so sánh không lệch hoa/thường hay khoảng trắng.
            const eventName = message.event?.trim().toUpperCase();

            // Keepalive - không mang transcript thật, bỏ qua hoàn toàn.
            if (eventName === STT_EVENT.PING) {
              return;
            }

            // Timeout hoàn toàn do server: NO_SPEECH_TIMEOUT → dừng mic + auto-submit nội dung đang có.
            // Text của event này là thông báo ("Im lặng quá lâu...") chứ không phải transcript
            // nên KHÔNG đưa vào input. Server đã tự kết thúc phiên nên không gửi STOP.
            if (eventName === STT_EVENT.NO_SPEECH_TIMEOUT) {
              console.log('[STT-gRPC] Server NO_SPEECH_TIMEOUT - stopping mic + auto-submit');
              const notify = finishingRef.current ? finishingRef.current.notify : true;
              stopCapture(false);
              if (notify) {
                onSilenceTimeoutRef.current?.();
              }
              return;
            }

            // Log để debug luồng event/text trả về từ gRPC.
            //   "SPEECH_ACTIVITY_BEGIN"  → user bắt đầu nói
            //   "SPEECH_ACTIVITY_END"    → user ngừng nói, đang xử lý ASR
            //   "END_OF_UTTERANCE"       → final text đã publish cho chatbot xử lý
            //   "NO_SPEECH_TIMEOUT"      → im lặng quá lâu, device về idle
            if (message.event) {
              console.log('[STT-gRPC] event:', message.event, '| status:', message.status, '| isFinal:', message.isFinal, '| text:', message.text);
            }

            // Lỗi stream: status=error hoặc event=error (text là message lỗi kỹ thuật, vd "Lỗi ASR
            // Stream: ...") → KHÔNG hiển thị text lên input, báo lỗi chung và dừng phiên.
            if (
              message.status?.toUpperCase() === STT_STATUS.ERROR.toUpperCase() ||
              eventName === STT_EVENT.ERROR
            ) {
              console.error('[STT-gRPC] Server reported STT error:', message.text);
              onErrorRef.current?.(MIC_ERROR_MESSAGE.GENERIC);
              stopCapture(false);
              return;
            }

            if (typeof message.text === 'string' && message.text.length > 0) {
              // Mỗi chunk trung gian đã chứa toàn bộ câu đang nói → chunk sau REPLACE chunk trước,
              // không nối từng chunk. Chỉ khi có event FINAL mới chốt câu và nối vào các câu đã chốt
              // trước đó trong lần bật mic này.
              const fullText = [committedTextRef.current, message.text].filter(Boolean).join(' ');
              console.log('[STT-gRPC] text:', fullText);
              onResultRef.current(fullText);
              if (eventName === STT_EVENT.FINAL) {
                committedTextRef.current = fullText;
              }
            }

            // Đã gửi STOP và đang chờ: nhận được text cuối thì chốt phiên (đóng WS + auto-submit).
            if (
              finishingRef.current &&
              (eventName === STT_EVENT.FINAL || eventName === STT_EVENT.END_OF_UTTERANCE)
            ) {
              finalizeCapture();
            }
            // Ngoài lúc chờ sau STOP, FINAL chỉ là server chốt xong một câu - KHÔNG dừng mic,
            // tiếp tục thu cho tới khi server báo NO_SPEECH_TIMEOUT hoặc đóng WS.
          };

          ws.onerror = () => {
            console.error('[STT-gRPC] WebSocket error');
            onErrorRef.current?.(MIC_ERROR_MESSAGE.GENERIC);
          };

          ws.onclose = () => {
            // stopCapture gỡ handler trước khi tự đóng, nên vào đây nghĩa là server chủ động đóng
            // (vd. kết thúc phiên sau final) → dừng mic + auto-submit như khi hết timeout.
            if (wsRef.current === ws && !discardRef.current) {
              if (finishingRef.current) {
                // Server đóng sau khi nhận STOP → chốt phiên theo cờ notify đã lưu.
                finalizeCapture();
                return;
              }
              console.log('[STT-gRPC] WebSocket closed by server - auto stopping mic + auto-submit');
              stopCapture(false);
              onSilenceTimeoutRef.current?.();
              return;
            }
            setIsListening(false);
          };
        } catch (error) {
          console.error('[STT-gRPC] Error setting up audio pipeline:', error);
          onErrorRef.current?.(MIC_ERROR_MESSAGE.GENERIC);
          teardownAudioGraph();
        }
      })
      .catch((err) => {
        if (!shouldStartRef.current) {
          console.log('[STT-gRPC] getUserMedia failed but STT was already stopped/cancelled');
          return;
        }
        console.error('[STT-gRPC] getUserMedia error:', err);
        const errName = err?.name || '';
        const errType = mapMediaErrorToType(errName);
        if (errType !== 'PERMISSION_DENIED') {
          onErrorTypeRef.current?.(errType);
        }
        const code = getMediaErrorCode(errName);
        if (code === 'not-allowed') {
          onErrorRef.current?.(MIC_ERROR_MESSAGE.NOT_ALLOWED, code);
        } else if (code === 'not-found') {
          onErrorRef.current?.(MIC_ERROR_MESSAGE.NOT_FOUND, code);
        } else if (code === 'in-use') {
          onErrorRef.current?.(MIC_ERROR_MESSAGE.NOT_READABLE, code);
        } else {
          onErrorRef.current?.(MIC_ERROR_MESSAGE.GENERIC, code);
        }
      });
  }, [isListening, isSupported, stopCapture, finalizeCapture, teardownAudioGraph]);

  // User bấm dừng: cũng chờ text cuối như auto-stop nhưng không auto-submit (giữ nghiệp vụ cũ).
  const stop = useCallback(() => {
    shouldStartRef.current = false;
    gracefulStop(false);
  }, [gracefulStop]);

  // Stop ngay lập tức, KHÔNG gửi STOP/xử lý transcript (dùng khi send manual hoặc tắt chatbot)
  const stopAndDiscard = useCallback(() => {
    shouldStartRef.current = false;
    discardRef.current = true;
    stopCapture(false);
  }, [stopCapture]);

  // Cleanup khi unmount
  useEffect(() => {
    return () => {
      shouldStartRef.current = false;
      discardRef.current = true;
      stopCapture(false);
    };
  }, []);

  return {
    start,
    stop,
    stopAndDiscard,
    isListening,
    isSupported,
  };
}

export default useGrpcSpeechToText;
